import React, { forwardRef } from 'react';
import { Icon } from './Icon';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'size'> {
  variant?: Variant;
  size?: Size;
  icon?: string;
  iconEnd?: string;
  loading?: boolean;
  fullWidth?: boolean;
}

const VARIANT: Record<Variant, string> = {
  primary:   'bg-primary hover:bg-primary-700 text-white shadow-xl shadow-primary/20 hover:shadow-primary/30 disabled:bg-slate-300 disabled:shadow-none',
  secondary: 'bg-white hover:bg-slate-50 text-slate-900 border-2 border-slate-200 hover:border-slate-300 disabled:text-slate-400 disabled:border-slate-100',
  ghost:     'bg-transparent hover:bg-slate-100 text-slate-700 hover:text-slate-900 disabled:text-slate-300',
  danger:    'bg-red-600 hover:bg-red-700 text-white shadow-xl shadow-red-600/20 disabled:bg-slate-300 disabled:shadow-none',
  success:   'bg-emerald-600 hover:bg-emerald-700 text-white shadow-xl shadow-emerald-600/20 disabled:bg-slate-300 disabled:shadow-none',
};

const SIZE: Record<Size, string> = {
  sm: 'h-9 px-3 text-xs gap-1.5 rounded-lg',
  md: 'h-11 px-5 text-sm gap-2 rounded-xl',
  lg: 'h-12 px-6 text-base gap-2 rounded-xl',
};

const ICON_SIZE: Record<Size, number> = { sm: 16, md: 18, lg: 20 };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', icon, iconEnd, loading, fullWidth, disabled, className = '', children, ...rest }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={[
          'inline-flex items-center justify-center font-bold transition-all',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed',
          VARIANT[variant],
          SIZE[size],
          fullWidth ? 'w-full' : '',
          className,
        ].filter(Boolean).join(' ')}
        {...rest}
      >
        {loading && <Icon name="progress_activity" size={ICON_SIZE[size]} className="animate-spin" />}
        {!loading && icon && <Icon name={icon} size={ICON_SIZE[size]} />}
        {children && <span>{children}</span>}
        {!loading && iconEnd && <Icon name={iconEnd} size={ICON_SIZE[size]} />}
      </button>
    );
  }
);
Button.displayName = 'Button';
